import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { authService, type LoginCredenciais } from "@/core/services/authService";
import { pessoasService, type Pessoa } from "@/core/services/pessoasService";
import { acessoService } from "@/core/services/acessoService";
import { pushService } from "@/core/services/pushService";

/**
 * Tempo máximo aceitável para o bootstrap (sessão + dados do Núcleo) antes de
 * desistir e mostrar um estado recuperável em vez de travar em "Carregando..."
 * para sempre (bug relatado, Task #11 / BUG-08 da auditoria de 20/07/2026:
 * uma única chamada de rede travada — sem timeout nem AbortController — deixava
 * `carregando` true indefinidamente, exigindo F5 manual do usuário).
 */
const TIMEOUT_BOOTSTRAP_MS = 12000;

class TimeoutError extends Error {}

function comTimeout<T>(promessa: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const temporizador = setTimeout(() => reject(new TimeoutError("Tempo esgotado.")), ms);
    promessa.then(
      (valor) => {
        clearTimeout(temporizador);
        resolve(valor);
      },
      (erro) => {
        clearTimeout(temporizador);
        reject(erro);
      },
    );
  });
}

interface AuthContextValue {
  session: Session | null;
  /**
   * Fonte única de identidade desde a Fase 6 (contract): a Pessoa do Núcleo
   * (tabela `pessoas`). O `perfil` legado (tabela `perfis`) foi aposentado na
   * etapa 6.2 — o critério de login agora é `pessoa.status`, e as buscas de
   * empréstimos usam `pessoa.id` (a coluna `emprestimos.pessoa_id` existe e
   * está 100% preenchida desde a migration 0021).
   */
  pessoa: Pessoa | null;
  /** Nível 1 (DA-002): Perfil Administrador da Plataforma. */
  ehAdminPlataforma: boolean;
  /** Nível 2 (DA-002): Função Ministerial com pode_gerenciar_modulo no Ministério Biblioteca. */
  podeGerenciarBiblioteca: boolean;
  /** Nível 2 (DA-002): gestão do módulo Eventos (Sprint 3). */
  podeGerenciarEventos: boolean;
  /** Nível 2 (DA-002): gestão do módulo Reservas (Sprint 3). */
  podeGerenciarReservas: boolean;
  /** Admin OU líder/gestor de algum ministério vinculado a Reservas — aprova pendentes (migration 0029). */
  podeAprovarReservas: boolean;
  /** G6: códigos dos módulos visíveis para esta pessoa (função modulos_visiveis, migration 0023). */
  modulosVisiveis: string[];
  carregando: boolean;
  /**
   * Preenchido quando o bootstrap (sessão + dados do Núcleo) falha ou demora
   * demais (BUG-08). Quando não-nulo, a tela deve oferecer "Tentar novamente"
   * em vez de ficar presa num spinner infinito.
   */
  erroCarregamento: string | null;
  login: (credenciais: LoginCredenciais) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Recarrega Pessoa + portões do DA-002 + módulos visíveis sem relogar.
   * Usado quando algo muda o acesso da própria pessoa no meio da sessão
   * (ex.: aceitar um convite de ministério — DA-003).
   */
  recarregarAcessos: () => Promise<void>;
  /** Refaz o bootstrap do zero após um erro/timeout (BUG-08), sem exigir F5. */
  tentarNovamente: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [pessoa, setPessoa] = useState<Pessoa | null>(null);
  const [ehAdminPlataforma, setEhAdminPlataforma] = useState(false);
  const [podeGerenciarBiblioteca, setPodeGerenciarBiblioteca] = useState(false);
  const [podeGerenciarEventos, setPodeGerenciarEventos] = useState(false);
  const [podeGerenciarReservas, setPodeGerenciarReservas] = useState(false);
  const [podeAprovarReservas, setPodeAprovarReservas] = useState(false);
  const [modulosVisiveis, setModulosVisiveis] = useState<string[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarregamento, setErroCarregamento] = useState<string | null>(null);
  const montadoRef = useRef(true);

  /**
   * Carrega os dados do Núcleo: a Pessoa correspondente ao usuário logado e
   * os portões de autorização do DA-002. Retorna a Pessoa carregada (ou
   * null) para que `login` possa decidir se aceita a sessão.
   */
  async function carregarDadosNucleo(userId: string): Promise<Pessoa | null> {
    try {
      const [
        pessoaCarregada,
        admPlataforma,
        gereBiblioteca,
        gereEventos,
        gereReservas,
        aprovaReservas,
        modulos,
      ] = await comTimeout(
        Promise.all([
          pessoasService.buscarPorUserId(userId).catch(() => null),
          acessoService.ehAdminPlataforma(),
          acessoService.podeGerenciarBiblioteca(),
          acessoService.podeGerenciarEventos(),
          acessoService.podeGerenciarReservas(),
          acessoService.podeAprovarReservas(),
          acessoService.listarModulosVisiveis(),
        ]),
        TIMEOUT_BOOTSTRAP_MS,
      );
      setPessoa(pessoaCarregada);
      setEhAdminPlataforma(admPlataforma);
      setPodeGerenciarBiblioteca(gereBiblioteca);
      setPodeGerenciarEventos(gereEventos);
      setPodeGerenciarReservas(gereReservas);
      setPodeAprovarReservas(aprovaReservas);
      setModulosVisiveis(modulos);
      return pessoaCarregada;
    } catch (erro) {
      limparDadosNucleo();
      if (erro instanceof TimeoutError) {
        throw erro;
      }
      return null;
    }
  }

  function limparDadosNucleo() {
    setPessoa(null);
    setEhAdminPlataforma(false);
    setPodeGerenciarBiblioteca(false);
    setPodeGerenciarEventos(false);
    setPodeGerenciarReservas(false);
    setPodeAprovarReservas(false);
    setModulosVisiveis([]);
  }

  async function inicializar() {
    setErroCarregamento(null);
    try {
      const sessaoAtual = await comTimeout(authService.getSessaoAtual(), TIMEOUT_BOOTSTRAP_MS);
      if (!montadoRef.current) return;

      setSession(sessaoAtual);
      if (sessaoAtual?.user) {
        await carregarDadosNucleo(sessaoAtual.user.id);
      }
      if (!montadoRef.current) return;
      setCarregando(false);
    } catch (erro) {
      if (!montadoRef.current) return;
      // Timeout ou falha de rede no bootstrap: não fica preso em "Carregando..."
      // para sempre — mostra um estado recuperável com "Tentar novamente"
      // (BUG-08). Uma sessão real, se existir, será recuperada no retry.
      setErroCarregamento(
        erro instanceof TimeoutError
          ? "A conexão está demorando mais que o esperado."
          : "Não foi possível carregar sua sessão.",
      );
      setCarregando(false);
    }
  }

  useEffect(() => {
    montadoRef.current = true;

    inicializar();

    // IMPORTANTE (bug do PWA "abre e fica em Carregando..."): este callback NÃO
    // pode aguardar (`await`) chamadas ao Supabase. (A assinatura exigida pelo
    // auth-js é `async`, mas o corpo só dispara trabalho e retorna.) O auth-js executa os
    // callbacks DENTRO do lock de autenticação (inclusive no refresh de um
    // token expirado, que é o que acontece na primeira abertura do app depois
    // de um tempo parado). Qualquer chamada ao Supabase feita aqui precisa do
    // mesmo lock (para ler o token) e fica esperando o próprio callback
    // terminar — deadlock até o timeout do bootstrap. Por isso o carregamento
    // dos dados do Núcleo é disparado sem `await`: o callback devolve o
    // controle na hora, o lock é liberado e as chamadas seguem normalmente.
    const { data: subscription } = authService.onAuthStateChange(async (evento, novaSessao) => {
      if (!montadoRef.current) return;
      setSession(novaSessao);

      // INITIAL_SESSION: o `inicializar()` acima já carrega sessão + dados do
      // Núcleo; repetir aqui duplicava todas as chamadas do bootstrap.
      if (evento === "INITIAL_SESSION") return;

      if (novaSessao?.user) {
        carregarDadosNucleo(novaSessao.user.id)
          .then(() => {
            if (montadoRef.current) setErroCarregamento(null);
          })
          .catch(() => {
            // Timeout num evento de auth em segundo plano (ex.: refresh de
            // token): não derruba a sessão já carregada, só registra o erro
            // para uma futura tentativa manual não ficar escondida.
          });
      } else {
        limparDadosNucleo();
      }
    });

    return () => {
      montadoRef.current = false;
      subscription.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function login(credenciais: LoginCredenciais) {
    await authService.login(credenciais);

    // O Supabase Auth já validou e-mail + senha nesse ponto, mas isso não
    // basta: a regra de negócio exige pessoa.status === "ativo" (Fase 6 —
    // antes era perfil.status, na tabela espelho `perfis`, hoje aposentada).
    // onAuthStateChange (acima) ainda não rodou de forma síncrona aqui,
    // então buscamos a pessoa diretamente para decidir se a sessão deve
    // ser mantida ou revertida.
    const sessaoAtual = await authService.getSessaoAtual();
    if (!sessaoAtual?.user) {
      throw new Error("Não foi possível iniciar a sessão. Tente novamente.");
    }

    const pessoaCarregada = await carregarDadosNucleo(sessaoAtual.user.id).catch(() => null);

    if (!pessoaCarregada || pessoaCarregada.status !== "ativo") {
      await authService.logout();
      setSession(null);
      limparDadosNucleo();

      if (pessoaCarregada?.status === "inativo") {
        throw new Error("Seu acesso foi desativado. Contate um administrador.");
      }
      throw new Error("Seu cadastro ainda está pendente de aprovação por um administrador.");
    }
  }

  /**
   * Desativa (nunca apaga — item 4 da decisão de lifecycle de push,
   * 03/09/2026) a subscription de push DESTE dispositivo antes de encerrar
   * a sessão. Melhor esforço: qualquer falha aqui (sem SW registrado, sem
   * rede, sem subscription) nunca deve impedir o logout — a subscription
   * fica ativa=true até o próximo dispatch encontrar 404/410 do provedor e
   * desativar sozinha (Fase 2, já em produção no DSV).
   */
  async function desativarPushSubscriptionAtual() {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return;
      await pushService.desativarPorEndpoint(subscription.endpoint);
    } catch {
      // Melhor esforço — ver comentário acima.
    }
  }

  async function logout() {
    await desativarPushSubscriptionAtual();
    await authService.logout();
  }

  async function recarregarAcessos() {
    const sessaoAtual = await authService.getSessaoAtual();
    if (sessaoAtual?.user) {
      await carregarDadosNucleo(sessaoAtual.user.id).catch(() => null);
    }
  }

  async function tentarNovamente() {
    setCarregando(true);
    await inicializar();
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        pessoa,
        ehAdminPlataforma,
        podeGerenciarBiblioteca,
        podeGerenciarEventos,
        podeGerenciarReservas,
        podeAprovarReservas,
        modulosVisiveis,
        carregando,
        erroCarregamento,
        login,
        logout,
        recarregarAcessos,
        tentarNovamente,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext deve ser usado dentro de um AuthProvider.");
  }
  return context;
}
